"use client";
import React, { createContext, useCallback, useContext, useState, useEffect } from "react";
import classNames from "classnames";
import Collapse from "react-bootstrap/es/Collapse";

type PanelGroupContextValue = {
  getId?: (key: any, type: "heading" | "body") => string | null;
  activeKey?: any;
  onToggle?: (eventKey: any, expanded: boolean, e: any) => void;
};

const PanelGroupContext = createContext<PanelGroupContextValue | null>(null);

type PanelContextValue = {
  headingId?: string | null;
  bodyId?: string | null;
  bsClass: string;
  expanded: boolean;
  onToggle: (e: any) => void;
};

const PanelContext = createContext<PanelContextValue | null>(null);

export interface PanelGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect"> {
  accordion?: boolean;
  activeKey?: any;
  defaultActiveKey?: any;
  onSelect?: (key: any, e: any) => void;
  id?: string;
}

export const PanelGroup: React.FC<PanelGroupProps> = ({
  accordion = false,
  activeKey,
  defaultActiveKey,
  onSelect,
  id,
  children,
  className,
  role,
  ...rest
}) => {
  // Initialize with defaultActiveKey if uncontrolled, otherwise use activeKey
  const [uncontrolledActiveKey, setUncontrolledActiveKey] = useState<any | undefined>(
    typeof activeKey !== "undefined" ? activeKey : defaultActiveKey,
  );

  const isControlled = typeof activeKey !== "undefined";
  const currentActiveKey = isControlled ? activeKey : uncontrolledActiveKey;

  useEffect(() => {
    if (isControlled) {
      setUncontrolledActiveKey(activeKey);
    }
  }, [isControlled, activeKey]);

  const getId =
    accordion && id ? (key: any, type: "heading" | "body") => `${id}-${type}-${key}` : undefined;

  const handleSelect = useCallback(
    (key: any, e: any) => {
      if (!isControlled) {
        setUncontrolledActiveKey(key);
      }
      if (onSelect) onSelect(key, e);
    },
    [isControlled, onSelect],
  );

  const handleToggle = useCallback(
    (key: any, expanded: boolean, e: any) => {
      const nextKey = expanded ? key : null;
      handleSelect(nextKey, e);
    },
    [handleSelect],
  );

  const ctx: PanelGroupContextValue = {
    getId,
    activeKey: accordion ? currentActiveKey : undefined,
    onToggle: accordion ? handleToggle : undefined,
  };

  const resolvedRole = accordion ? role || "tablist" : role;

  return (
    <div {...rest} id={id} role={resolvedRole} className={classNames("panel-group", className)}>
      <PanelGroupContext.Provider value={ctx}>{children}</PanelGroupContext.Provider>
    </div>
  );
};

export interface PanelProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onToggle"> {
  eventKey?: any;
  expanded?: boolean;
  onToggle?: (expanded: boolean, e: any) => void;
  id?: string;
  bsClass?: string;
  bsStyle?: string;
}

const PanelBase: React.FC<PanelProps> = ({
  eventKey,
  expanded,
  onToggle,
  id,
  bsClass: bsClassProp = "panel",
  bsStyle: bsStyleProp = "default",
  children,
  className,
  ...rest
}) => {
  const group = useContext(PanelGroupContext);

  const getExpanded = () => {
    if (group && typeof group.activeKey !== "undefined") {
      return group.activeKey === eventKey;
    }
    return !!expanded;
  };

  const handleToggle = (e: any) => {
    const next = !getExpanded();
    if (group && group.onToggle) {
      group.onToggle(eventKey, next, e);
    } else if (onToggle) {
      onToggle(next, e);
    }
  };

  const idKey = eventKey == null ? id : eventKey;
  let headingId: string | null | undefined;
  let bodyId: string | null | undefined;
  if (idKey != null && group && group.getId) {
    headingId = group.getId(idKey, "heading");
    bodyId = group.getId(idKey, "body");
  }

  const panelCtx: PanelContextValue = {
    headingId,
    bodyId,
    bsClass: bsClassProp,
    expanded: getExpanded(),
    onToggle: handleToggle,
  };

  const panelClass = classNames(className, "panel", `panel-${bsStyleProp}`);

  return (
    <div {...rest} className={panelClass}>
      <PanelContext.Provider value={panelCtx}>{children}</PanelContext.Provider>
    </div>
  );
};

export interface PanelHeadingProps extends React.HTMLAttributes<HTMLDivElement> {}

const PanelHeading: React.FC<PanelHeadingProps> = ({ className, children, ...rest }) => {
  const ctx = useContext(PanelContext);
  const headingId = ctx?.headingId;
  const bsClass = ctx?.bsClass || "panel";

  const props: React.HTMLAttributes<HTMLDivElement> & {
    id?: string;
    role?: string;
  } = { ...rest };

  if (headingId) {
    if (!props.role) props.role = "tab";
    props.id = headingId;
  }

  return (
    <div {...props} className={classNames(className, `${bsClass}-heading`)}>
      {children}
    </div>
  );
};
export interface PanelToggleProps extends React.HTMLAttributes<HTMLElement> {
  componentClass?: React.ElementType;
}

const PanelToggle: React.FC<PanelToggleProps> = ({
  componentClass: Component = "a",
  className,
  onClick,
  ...rest
}) => {
  const ctx = useContext(PanelContext);
  const expanded = !!ctx?.expanded;
  const bodyId = ctx?.bodyId;

  const handleClick = (e: any) => {
    // Prevent default anchor navigation (no scrolling to top or '#' in URL)
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    if (ctx?.onToggle) {
      ctx.onToggle(e);
    }
    if (onClick) onClick(e);
  };

  const props: any = {
    ...rest,
    onClick: handleClick,
    "aria-expanded": expanded,
    className: classNames(className, !expanded && "collapsed"),
  };

  if (bodyId) {
    props["aria-controls"] = bodyId;
  }

  // Match react-bootstrap: default to role="button" and href="#" for anchors
  if (!props.role) {
    props.role = "button";
  }
  if (Component === "a" && !props.href) {
    props.href = "#";
  }

  return <Component {...props} />;
};
export interface PanelTitleProps extends React.HTMLAttributes<HTMLDivElement> {
  toggle?: boolean;
  componentClass?: React.ElementType;
}

const PanelTitle: React.FC<PanelTitleProps> = ({
  toggle,
  componentClass: Component = "div",
  className,
  children,
  ...rest
}) => {
  const ctx = useContext(PanelContext);
  const bsClass = ctx?.bsClass || "panel";

  let content = children as React.ReactNode;
  if (toggle) {
    // Wrap children in an anchor-like toggle element, matching react-bootstrap v0.33.1
    content = <PanelToggle>{children}</PanelToggle>;
  }

  return (
    <Component {...rest} className={classNames(className, `${bsClass}-title`)}>
      {content}
    </Component>
  );
};

export interface PanelBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  collapsible?: boolean;
}

const PanelCollapse: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  children,
  ...rest
}) => {
  const ctx = useContext(PanelContext);
  const expanded = !!ctx?.expanded;
  const headingId = ctx?.headingId;
  const bodyId = ctx?.bodyId;
  const bsClass = ctx?.bsClass || "panel";

  const props: React.HTMLAttributes<HTMLDivElement> & {
    id?: string;
    role?: string;
    "aria-labelledby"?: string;
  } = { ...rest };

  if (headingId && bodyId) {
    props.id = bodyId;
    if (!props.role) props.role = "tabpanel";
    (props as any)["aria-labelledby"] = headingId;
  }

  (props as any)["aria-expanded"] = expanded;

  const collapseClass = classNames(`${bsClass}-collapse`, className, { show: expanded });

  return (
    <Collapse in={expanded}>
      <div {...props} className={collapseClass}>
        {children}
      </div>
    </Collapse>
  );
};

const PanelBody: React.FC<PanelBodyProps> = ({ collapsible, className, children, ...rest }) => {
  const ctx = useContext(PanelContext);
  const bsClass = ctx?.bsClass || "panel";

  const body = (
    <div {...rest} className={classNames(className, `${bsClass}-body`)}>
      {children}
    </div>
  );

  if (collapsible) {
    return <PanelCollapse>{body}</PanelCollapse>;
  }

  return body;
};

export const Panel: any = PanelBase as any;

Panel.Heading = PanelHeading;
Panel.Title = PanelTitle;
Panel.Body = PanelBody;
Panel.Collapse = PanelCollapse;
Panel.Toggle = PanelToggle;

export default Panel;
